import type { TemplateDecisionClass, TemplateForceTotalDto, TemplateListItemDto } from '@pams/shared';

/**
 * [P7B.1 §2] What a Part's or a section's pane derives from the list — pure, and in a `.ts` file
 * because `react-refresh/only-export-components` refuses a helper exported beside a component.
 */

/** Every template's people, summed per column and source — the matrix of a Part or a section. */
export const sumForce = (items: ReadonlyArray<Pick<TemplateListItemDto, 'force'>>): TemplateForceTotalDto[] => {
  const totals = new Map<string, Map<string, number>>();
  for (const item of items) {
    for (const total of item.force) {
      const sources = totals.get(total.column) ?? new Map<string, number>();
      sources.set(total.source, (sources.get(total.source) ?? 0) + total.n);
      totals.set(total.column, sources);
    }
  }
  return [...totals].flatMap(([column, sources]) => [...sources].map(([source, n]) => ({ column, source, n })));
};

/**
 * The codes still open across some templates — a matrix header or row reads as the inverted chip.
 * [TEMPLATE-FIND] Any class: the template pane also asks it for its open printed totals and duty points.
 */
export const openCodesIn = (
  items: ReadonlyArray<Pick<TemplateListItemDto, 'waitsOn'>>,
  decisionClass: TemplateDecisionClass,
): Set<string> =>
  new Set(
    items.flatMap((item) =>
      item.waitsOn.flatMap((wait) => (wait.class === decisionClass && wait.code !== null ? [wait.code] : [])),
    ),
  );

/**
 * The people a template's duty points ask, summed straight from its groups — round the clock included.
 * The server's `force` leaves round the clock out, which is right for what will be built; a template
 * flagged round the clock WHOLE builds nothing, so its pane shows the booklet's figures from here,
 * captioned as never built.
 */
export const forceOfGroups = (
  groups: ReadonlyArray<{
    readonly dutyPoints: ReadonlyArray<{
      readonly force: ReadonlyArray<{ readonly column: string; readonly n: number; readonly source: string | null }>;
    }>;
  }>,
  blankSource: string,
): TemplateForceTotalDto[] =>
  sumForce([
    {
      force: groups.flatMap((group) =>
        group.dutyPoints.flatMap((point) =>
          point.force.flatMap((row) =>
            row.n > 0 ? [{ column: row.column, source: row.source ?? blankSource, n: row.n }] : [],
          ),
        ),
      ),
    },
  ]);
